import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import CyberCubLogo from '../../components/CyberCubLogo.jsx';
import Tutor from '../../components/Tutor.jsx';
import LinuxDesktopArt from './LinuxDesktop.jsx';
import { useFinished } from '../../lib/practical.js';

/**
 * Practical site for "Linux Basics".
 *
 * The same three sections as the Windows practical, deliberately: a
 * desktop to find your way around, real paths to read, and the question
 * of who is allowed to do what. Learning the second operating system is
 * mostly a matter of noticing what is in the same place and what is
 * not, and that is far easier when the two lessons have the same shape.
 *
 * Where Linux differs, it differs on purpose and the section says so -
 * one tree instead of drive letters, a dot instead of a hidden
 * attribute, and a permission bit rather than a file extension deciding
 * what will run.
 */

const STORE_KEY = 'cybercub.lab.linux-basics';

/* ------------------------------------------------------------------ */
/* 1. find your way around the desktop                                */
/* ------------------------------------------------------------------ */

/*
 * Hotspots, in percentages of the drawn desktop so they stay put at
 * whatever size the picture ends up. Each sits over a different part,
 * with no overlap - the dock's own region is a bare stretch of it, away
 * from the icons pinned to it.
 */
const SPOTS = [
  {
    id: 'activities',
    name: 'Activities',
    x: 1.5, y: 0.5, w: 11, h: 5,
    ask: 'Where do you click to see everything that is open, and to search for a program?',
    why: 'Activities. It shows every open window and searches the whole machine — the job the Start button does on Windows.',
  },
  {
    id: 'dock',
    name: 'The dock',
    x: 1, y: 62, w: 7.5, h: 7,
    ask: 'Which strip holds the programs you use most?',
    why: 'The dock. Pinned programs live there, and a dot beside one means it is running right now.',
  },
  {
    id: 'files',
    name: 'Files',
    x: 1.6, y: 10.5, w: 5.5, h: 8.5,
    ask: 'Which icon opens the files and folders on this machine?',
    why: 'Files — the same job as File Explorer. It is how you reach anything stored on the disk.',
  },
  {
    id: 'terminal',
    name: 'Terminal',
    x: 1.6, y: 30.5, w: 5.5, h: 8.5,
    ask: 'Which icon opens the window you type commands into?',
    why: 'Terminal. On Linux this is not a last resort — it is where most real work gets done, which is why it is pinned by default.',
  },
  {
    id: 'system',
    name: 'System menu',
    x: 88, y: 0.5, w: 11, h: 5,
    ask: 'Where are the network, sound and power controls?',
    why: 'The system menu, top right. Wi-Fi, volume, battery and the button that logs you out.',
  },
];

/* ------------------------------------------------------------------ */
/* 2. read a path                                                     */
/* ------------------------------------------------------------------ */

const PATHS = [
  {
    id: 'p1',
    path: '/home/aarav/Documents/homework.odt',
    q: 'Whose folder is this file in?',
    options: ['Nobody — it is a system file', "Aarav's", 'Everyone on the machine', 'The root account'],
    answer: 1,
    why: 'Everything under /home/<name> belongs to that person. It is exactly where C:\\Users\\<name> sits on Windows, with a different spelling.',
  },
  {
    id: 'p2',
    path: '/etc/ssh/sshd_config',
    q: 'What lives in /etc?',
    options: [
      'Your documents',
      'The settings for the machine itself',
      'Programs you can run',
      'Files that get thrown away on restart',
    ],
    answer: 1,
    why: '/etc is the settings drawer for the whole system. Change a file in there and you change how the machine behaves for everybody.',
  },
  {
    id: 'p3',
    path: '/var/log/auth.log',
    q: 'What would you expect to find in this file?',
    options: [
      'A list of installed programs',
      'A record of who signed in, and who tried and failed',
      'The machine’s wallpaper',
      'Downloaded files',
    ],
    answer: 1,
    why: 'Logs live under /var/log, and auth.log is the sign-in record. It is the first file anybody reads after a break-in — and the first one an intruder would like to edit.',
  },
  {
    id: 'p4',
    path: '/root/',
    q: 'What is /root?',
    options: [
      'The top of the whole disk',
      'The home folder of the root account',
      'Where the system boots from',
      'A folder everybody shares',
    ],
    answer: 1,
    why: 'The top of the disk is just / on its own. /root is a home folder like any other — it simply belongs to the account that can do anything.',
  },
  {
    id: 'p5',
    path: '/home/aarav/.bashrc',
    q: 'Why does ls not show this file?',
    options: [
      'It is broken',
      'Its name starts with a dot, which on Linux means hidden',
      'It belongs to another user',
      'It is too small',
    ],
    answer: 1,
    why: 'A leading dot is the whole mechanism. ls -a shows it — which means hiding a file here is a naming convention, not security, exactly as it is on Windows.',
  },
  {
    id: 'p6',
    path: '/home/meera/Downloads/update.sh',
    q: 'Does the .sh on the end mean this file will run?',
    options: [
      'Yes — .sh files always run',
      'No — Linux decides by a permission bit, not by the name',
      'Only if it is a picture',
      'Only inside /home',
    ],
    answer: 1,
    why: 'The name is a hint to humans. Linux runs a file only if it is marked executable, which is why chmod +x is a step somebody has to take on purpose.',
  },
];

/* ------------------------------------------------------------------ */
/* 3. who is allowed?                                                 */
/* ------------------------------------------------------------------ */

const RIGHTS = [
  {
    id: 'r1',
    icon: '🖼️',
    text: 'Aarav wants to change the desktop wallpaper on his own account.',
    options: ['Needs root (sudo)', 'A normal user can do this'],
    answer: 1,
    why: 'It changes only his own settings. Nothing outside his home folder moves.',
  },
  {
    id: 'r2',
    icon: '📦',
    text: 'A program wants to install itself into /usr/bin, where everybody can run it.',
    options: ['Needs root (sudo)', 'A normal user can do this'],
    answer: 0,
    why: 'Anything outside your own home folder belongs to the system, and writing there needs root. That is why installing software asks for a password.',
  },
  {
    id: 'r3',
    icon: '🗄️',
    text: 'Something wants to edit /etc/passwd, the list of accounts on the machine.',
    options: ['Needs root (sudo)', 'A normal user can do this'],
    answer: 0,
    why: 'That file decides who exists on this machine. If a program you did not expect is asking to change it, the answer is no.',
  },
  {
    id: 'r4',
    icon: '📄',
    text: 'Meera saves an essay into /home/meera/Documents.',
    options: ['Needs root (sudo)', 'A normal user can do this'],
    answer: 1,
    why: 'Her folder, her file. This is what a normal account is for, and it is most of what anybody does all day.',
  },
];

/* ------------------------------------------------------------------ */

const load = () => {
  try { return JSON.parse(localStorage.getItem(STORE_KEY) ?? '{}'); } catch { return {}; }
};
const save = (state) => {
  try { localStorage.setItem(STORE_KEY, JSON.stringify(state)); } catch { /* private window */ }
};

export default function LinuxBasicsLab() {
  const saved = useMemo(load, []);

  const [tab, setTab] = useState('desktop');
  const [found, setFound] = useState(saved.found ?? []);
  const [wrong, setWrong] = useState(null);
  const [pathPicks, setPathPicks] = useState(saved.pathPicks ?? {});
  const [rightPicks, setRightPicks] = useState(saved.rightPicks ?? {});

  useEffect(() => {
    save({ found, pathPicks, rightPicks });
  }, [found, pathPicks, rightPicks]);

  useEffect(() => { document.title = 'Linux Basics · CyberCub Lab'; }, []);

  const asking = SPOTS.find((s) => !found.includes(s.id)) ?? null;
  const desktopDone = found.length === SPOTS.length;
  const pathsDone = PATHS.every((p) => pathPicks[p.id] === p.answer);
  const rightsDone = RIGHTS.every((r) => rightPicks[r.id] === r.answer);

  useFinished('/lab/linux-basics', rightsDone);

  const TABS = [
    { id: 'desktop', n: 1, label: 'Find your way around', locked: false },
    { id: 'paths', n: 2, label: 'Read a path', locked: !desktopDone },
    { id: 'rights', n: 3, label: 'Who is allowed?', locked: !pathsDone },
  ];

  const pick = (id) => {
    if (!asking) return;
    if (id === asking.id) {
      setWrong(null);
      setFound((f) => [...f, id]);
      return;
    }
    setWrong(id);
    setTimeout(() => setWrong(null), 700);
  };

  const back = (() => {
    if (tab === 'desktop') return null;
    if (tab === 'paths') return { label: '← Back to the desktop', act: () => setTab('desktop') };
    return { label: '← Back to the paths', act: () => setTab('paths') };
  })();

  return (
    <div className="lab">
      <header className="lab-top">
        <div className="row gap-sm">
          <CyberCubLogo size={38} />
          <div>
            <strong>Linux Basics</strong>
            <span className="lab-sub">Practical · Ethical Hacker, Module 2</span>
          </div>
        </div>
        <Link to="/learn/lesson/cf4-linux-basics" className="btn btn-sm btn-ghost">
          ← Back to the lesson
        </Link>
      </header>

      <nav className="lab-tabs" aria-label="Sections">
        {TABS.map((t) => (
          <button
            key={t.id}
            type="button"
            className={`lab-tab ${tab === t.id ? 'is-active' : ''} ${t.locked ? 'is-locked' : ''}`}
            onClick={() => !t.locked && setTab(t.id)}
            disabled={t.locked}
            aria-current={tab === t.id ? 'page' : undefined}
          >
            <span className="lab-tab-n">{t.n}</span>
            {t.label}
            {t.locked && <span aria-label="locked"> 🔒</span>}
          </button>
        ))}
      </nav>

      {/* ---------------- 1. the desktop ---------------- */}
      {tab === 'desktop' && (
        <section className="lab-section">
          <div className="place-head">
            <div>
              <h2>Find your way around</h2>
              <p className="lab-lead">
                A Linux desktop with nothing labelled. Read what is being asked for, then click it on
                the picture.
              </p>
            </div>
            <span className="pill">{found.length} / {SPOTS.length} found</span>
          </div>

          <Tutor
            avatar="cub-sun"
            say={
              desktopDone
                ? 'All five found. Linux comes in a hundred flavours and they all arrange this differently — but every one of them has somewhere to search from, somewhere the pinned programs live, a way to reach your files, and a corner for the clock. Find those four and you can use any of them.'
                : asking
                  ? `Find this one: ${asking.ask}`
                  : 'Click the part of the desktop being asked for.'
            }
          />

          {asking && (
            <div className="win-ask">
              <span className="win-ask-n" aria-hidden="true">{found.length + 1}</span>
              <p>{asking.ask}</p>
            </div>
          )}

          <div className="win-desktop">
            <LinuxDesktopArt showFiles />
            {SPOTS.map((s) => {
              const done = found.includes(s.id);
              const isWrong = wrong === s.id;
              return (
                <button
                  key={s.id}
                  type="button"
                  className={`win-spot ${done ? 'is-found' : ''} ${isWrong ? 'is-wrong' : ''}`}
                  style={{ left: `${s.x}%`, top: `${s.y}%`, width: `${s.w}%`, height: `${s.h}%` }}
                  onClick={() => pick(s.id)}
                  aria-label={done ? `${s.name}, found` : 'Unlabelled part of the desktop'}
                  disabled={done || !asking}
                >
                  {done && <span className="win-spot-tag">{s.name}</span>}
                </button>
              );
            })}
          </div>

          {found.length > 0 && (
            <ul className="win-learned">
              {SPOTS.filter((s) => found.includes(s.id)).map((s) => (
                <li key={s.id}><strong>{s.name}.</strong> {s.why}</li>
              ))}
            </ul>
          )}

          {desktopDone && (
            <div className="lab-done">
              <strong>All five.</strong> Now find out where the files behind those icons actually live —
              and it is not a drive letter.
              <button type="button" className="btn btn-sm" onClick={() => setTab('paths')}>
                Read a path →
              </button>
            </div>
          )}
        </section>
      )}

      {/* ---------------- 2. read a path ---------------- */}
      {tab === 'paths' && (
        <section className="lab-section">
          <div className="place-head">
            <div>
              <h2>Read a path</h2>
              <p className="lab-lead">
                No drive letters here. Everything on a Linux machine hangs off one tree that starts
                at <code>/</code> — including the USB stick you just plugged in. Six real paths:
                work out what each is telling you.
              </p>
            </div>
            <div className="row gap-sm">
              <button type="button" className="btn btn-sm btn-ghost" onClick={back.act}>
                {back.label}
              </button>
              <span className="pill">
                {PATHS.filter((p) => pathPicks[p.id] === p.answer).length} / {PATHS.length}
              </span>
            </div>
          </div>

          <Tutor
            avatar="cub-grape"
            say={
              pathsDone
                ? 'All six. The one worth carrying out of here is the last: Linux does not care what a file is called. A file runs because somebody marked it runnable, not because it ends in .sh — which is the exact opposite of the trick that works so well on Windows.'
                : 'Read each path from the left. The first slash is the top of everything; after that it is folders all the way down. And notice what is missing — there is no C: anywhere, because there are no drive letters at all.'
            }
          />

          {PATHS.map((p) => {
            const pick2 = pathPicks[p.id];
            const right = pick2 === p.answer;
            return (
              <div key={p.id} className={`lab-case ${right ? 'is-right' : pick2 !== undefined ? 'is-wrong' : ''}`}>
                <div className="grow">
                  <code className="win-path">{p.path}</code>
                  <p><strong>{p.q}</strong></p>
                  <div className="lab-choices">
                    {p.options.map((opt, oi) => (
                      <button
                        key={opt}
                        type="button"
                        className={`lab-choice ${pick2 === oi ? 'is-picked' : ''}`}
                        onClick={() => setPathPicks((prev) => ({ ...prev, [p.id]: oi }))}
                        disabled={right}
                      >
                        {opt}
                      </button>
                    ))}
                  </div>
                  {pick2 !== undefined && (
                    <p className={`lab-feedback ${right ? 'is-right' : 'is-wrong'}`}>
                      {right ? `✓ ${p.why}` : '✗ Not that one — read the path again from the first slash.'}
                    </p>
                  )}
                </div>
              </div>
            );
          })}

          {pathsDone && (
            <div className="lab-done">
              <strong>All six read.</strong> One question left, and it decides how much damage anything
              can do.
              <button type="button" className="btn btn-sm" onClick={() => setTab('rights')}>
                Who is allowed? →
              </button>
            </div>
          )}
        </section>
      )}

      {/* ---------------- 3. who is allowed ---------------- */}
      {tab === 'rights' && (
        <section className="lab-section">
          <div className="place-head">
            <div>
              <h2>Who is allowed?</h2>
              <p className="lab-lead">
                Root can change anything on the machine. A normal user can only change their own
                things, and borrows root for one command at a time with <code>sudo</code>. For each
                of these, decide which is needed.
              </p>
            </div>
            <div className="row gap-sm">
              <button type="button" className="btn btn-sm btn-ghost" onClick={back.act}>
                {back.label}
              </button>
              <span className="pill">
                {RIGHTS.filter((r) => rightPicks[r.id] === r.answer).length} / {RIGHTS.length}
              </span>
            </div>
          </div>

          <Tutor
            avatar="cub-coral"
            say={
              rightsDone
                ? 'Same idea as Windows, different word. Anything that runs as you can do exactly what you can do — no more. Linux simply makes the boundary easier to see: your home folder is yours, and everything else needs sudo and a password.'
                : 'Ask one question each time: does this change only my own things, or does it change the machine for everybody? The second one needs root, and that is when sudo asks for a password.'
            }
          />

          {RIGHTS.map((r) => {
            const pick3 = rightPicks[r.id];
            const right = pick3 === r.answer;
            return (
              <div key={r.id} className={`lab-case ${right ? 'is-right' : pick3 !== undefined ? 'is-wrong' : ''}`}>
                <span className="lab-case-n" aria-hidden="true">{r.icon}</span>
                <div className="grow">
                  <p>{r.text}</p>
                  <div className="lab-choices">
                    {r.options.map((opt, oi) => (
                      <button
                        key={opt}
                        type="button"
                        className={`lab-choice ${pick3 === oi ? 'is-picked' : ''}`}
                        onClick={() => setRightPicks((prev) => ({ ...prev, [r.id]: oi }))}
                        disabled={right}
                      >
                        {oi === 0 ? '🛡️ ' : '👤 '}{opt}
                      </button>
                    ))}
                  </div>
                  {pick3 !== undefined && (
                    <p className={`lab-feedback ${right ? 'is-right' : 'is-wrong'}`}>
                      {right ? `✓ ${r.why}` : '✗ Not quite — does it change only this person’s things, or the whole machine?'}
                    </p>
                  )}
                </div>
              </div>
            );
          })}

          {rightsDone && (
            <div className="lab-done">
              <div>
                <strong>All four.</strong>
                <p style={{ margin: '0.3rem 0 0' }}>
                  A command that starts with sudo is a command asking to change the machine itself.
                  Typing your password in front of one you did not expect is the Linux version of
                  clicking Yes on a box you were not expecting — and it hands over exactly as much.
                </p>
              </div>
              <Link to="/learn/lesson/cf4-linux-basics" className="btn btn-sm">
                ← Back to the lesson
              </Link>
            </div>
          )}
        </section>
      )}
    </div>
  );
}
